import React, { useEffect, useState } from 'react';

import { Alert, Center, Loader, Stack, useMantineTheme } from '@mantine/core';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import { IconExclamationCircle } from '@tabler/icons-react';
import config from 'config';
import useStripeCheckoutSession from 'hooks/paymentMethod/useStripeCheckoutSession';
import { GymAthleteRetrieve } from 'types/generated/models/GymAthleteRetrieve';
import { GymRetrieve } from 'types/generated/models/GymRetrieve';

import { IAutoPayOnAttach } from '../types';

interface IAddPaymentMethodCSProps {
  athleteId: string;
  gym: GymRetrieve | GymAthleteRetrieve;
  setAsDefault: boolean;
  /** Set only when the staff auto-pay toggle is on offer; undefined leaves the flow as it was. */
  autoPay?: IAutoPayOnAttach;
  cancelHandler: () => void;
  successHandler: (paymentMethodId?: string) => void;
}

const AddPaymentMethodCS = ({
  athleteId,
  gym,
  setAsDefault,
  autoPay,
}: IAddPaymentMethodCSProps): JSX.Element => {
  const theme = useMantineTheme();

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | undefined>();

  // No counterpart here to the payment-element route's staff event or its query invalidation.
  // Stripe's own hosted form takes the submit and returns through the session's `return_url`, a
  // full page navigation, so this component has no success callback to fire an event from and no
  // client cache left to invalidate afterwards. Reading `staff_payment_method_auto_pay_selected`
  // therefore means reading the payment-element route only, which is every gym outside GB on a
  // standard Connect account. Recovering it means moving the session to
  // `redirect_on_completion: 'never'` and an `onComplete` handler, which changes the route's
  // return path for every caller and is not this PR's change.
  const { isLoading, isError, clientSecret, handleCreateCheckoutSession } = useStripeCheckoutSession({
    athleteId,
    returnPath: window.location.pathname,
    setAsDefault,
    // Sent as a concrete boolean rather than omitted when the toggle is not on offer, because
    // this value goes through our own endpoint into session metadata that outlives each edit.
    // Unchecking "Set as default" retracts the toggle mid-session, and an omission there would
    // leave a stale "true" behind instead of overwriting it.
    enableAutoPay: autoPay?.enabled ?? false,
  });

  useEffect(() => {
    handleCreateCheckoutSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStripePromise(
      loadStripe(config.STRIPE_PUBLIC_KEY, {
        stripeAccount: gym.stripeConnectAccount || '',
      }),
    );
  }, [gym]);

  if (isError) {
    return (
      <Center my='xl'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong!
        </Alert>
      </Center>
    );
  }

  return (
    <Stack>
      {isLoading || !clientSecret || !stripePromise ? (
        <Center my='xl'>
          <Loader />
        </Center>
      ) : (
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </Stack>
  );
};

export default AddPaymentMethodCS;
